import { useEffect, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppDispatch } from 'app/hooks';
import {
  projectBoardApi,
  useGetAhuTunnelsQuery,
} from 'app/services/projectBoardApi';
import { ahuTunnelAddress, type IAhuTunnel } from 'models';

/** One "Sections" pane line: "{tunnelIndex}   {name}[: {tag}]" (B1.4). */
export interface AhuSectionLine {
  index: number | string;
  name: string;
  /** Pre-rendered `: {tag}` suffix or '' (parity 143). */
  tag: string;
  tunnelId: number;
}

export interface AhuSectionsResult {
  tunnels: IAhuTunnel[] | undefined;
  /** null while loading — the `.full-ahu-preloader` stays `.active`. */
  sections: AhuSectionLine[] | null;
}

/**
 * Info-bar sections pipeline (viewProjectAhuUnit.getAhuSections, B1.4 /
 * parity 143): GET /ahu/tunnels/?ahu_id={id}, then one
 * GET /ahu/sections/?tunnel_id={id} per tunnel; accumulate
 * {index, name, tag} (rows without a name skipped), sort by section
 * tunnel_id, then the preloader deactivates. Per-tunnel fetches go through
 * the RTK cache (`initiate`) so re-selecting an AHU reuses cached data.
 *
 * [NEG] honored: no trailing `fn['get.all.cms.data']` (undefined key no-op).
 */
export function useAhuSections(ahuId: number | null): AhuSectionsResult {
  const dispatch = useAppDispatch();
  const { data: tunnels } = useGetAhuTunnelsQuery(ahuId ?? skipToken);
  const [sections, setSections] = useState<AhuSectionLine[] | null>(null);

  useEffect(() => {
    setSections(null);
    if (!tunnels || ahuId == null) return undefined;
    let cancelled = false;
    const subs = tunnels.map((t) =>
      dispatch(projectBoardApi.endpoints.getTunnelSections.initiate(t.id))
    );
    Promise.all(subs.map((s) => s.unwrap()))
      .then((results) => {
        if (cancelled) return;
        const acc: AhuSectionLine[] = [];
        results.forEach((rows, i) => {
          const tunnel = tunnels[i];
          rows.forEach((row) => {
            if (row.name === undefined) return;
            acc.push({
              index: ahuTunnelAddress(tunnel),
              name: row.name,
              // Legacy quirk kept bug-for-bug: project_ahu_unit.js prefixes
              // `: ` TWICE (mutates a.tag on line 150, prefixes again on 152),
              // so the live render is "{name} : : {tag}".
              tag: row.tag ? `: : ${row.tag}` : '',
              tunnelId: row.tunnel_id,
            });
          });
        });
        acc.sort((a, b) => a.tunnelId - b.tunnelId);
        setSections(acc);
      })
      .catch(() => {
        if (!cancelled) setSections([]);
      });
    return () => {
      cancelled = true;
      subs.forEach((s) => s.unsubscribe());
    };
  }, [tunnels, ahuId, dispatch]);

  return { tunnels, sections };
}
